// src/services/deviceManagementApi.js

import * as api from './api';
import * as signatureService from './signatureService';

/**
 * Base API URL
 */
const API_BASE_URL = 'https://api.aeronyx.network/api';

/**
 * Get device details by order ID
 * @param {string} orderId - The order ID to fetch device for
 * @param {Object} wallet - Wallet information
 * @returns {Promise<Object>} Response with device details
 */
export const getDeviceDetailsByOrderId = async (orderId, wallet) => {
  try {
    if (!orderId || !wallet.connected) {
      throw new Error('Order ID or wallet connection missing');
    }
    
    // Get network ID based on wallet network
    const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
    
    // Get signature using signature service
    const signatureData = await signatureService.getValidSignature(
      wallet.address,
      networkId,
      wallet.type
    );
    
    // Make API call to get device by order ID
    const response = await fetch(`${API_BASE_URL}/devices/order/${orderId}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: wallet.address,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: wallet.type
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch device details');
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching device details:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch device details',
    };
  }
};

/**
 * Check claim status for a device
 * @param {string} orderId - Order ID
 * @param {Object} wallet - Wallet information 
 * @returns {Promise<Object>} Response with claim status
 */
export const getDeviceClaimStatus = async (orderId, wallet) => {
  try {
    if (!orderId || !wallet.connected) {
      throw new Error('Order ID or wallet connection missing');
    }
    
    // Get network ID based on wallet network
    const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
    
    // Get signature using signature service
    const signatureData = await signatureService.getValidSignature(
      wallet.address,
      networkId,
      wallet.type
    );
    
    // Make API call to check claim status
    const response = await fetch(`${API_BASE_URL}/devices/order/${orderId}/claim-status/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: wallet.address,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: wallet.type
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to check claim status');
    }
    
    return result;
  } catch (error) {
    console.error('Error checking claim status:', error);
    return {
      success: false,
      message: error.message || 'Failed to check claim status',
    };
  }
};

/**
 * Claim a device with shipping information
 * @param {string} orderId - Order ID to claim devices for
 * @param {Object} wallet - Wallet information
 * @param {Object} shippingInfo - Shipping information
 * @returns {Promise<Object>} Response with claim result
 */
export const claimDevice = async (orderId, wallet, shippingInfo) => {
  try {
    if (!orderId || !wallet.connected) {
      throw new Error('Order ID or wallet connection missing');
    }
    
    if (!shippingInfo || !shippingInfo.fullName || !shippingInfo.streetAddress) {
      throw new Error('Shipping information is incomplete');
    }
    
    // Get network ID based on wallet network
    const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
    
    // Get signature using signature service
    const signatureData = await signatureService.getValidSignature(
      wallet.address,
      networkId,
      wallet.type
    );
    
    // Format shipping info to match API expectations
    const formattedShippingInfo = {
      full_name: shippingInfo.fullName,
      street_address: shippingInfo.streetAddress,
      city: shippingInfo.city,
      state: shippingInfo.state,
      postal_code: shippingInfo.postalCode,
      country: shippingInfo.country,
      phone_number: shippingInfo.phoneNumber
    };
    
    // Make API call to claim device
    const response = await fetch(`${API_BASE_URL}/devices/order/${orderId}/claim/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: wallet.address,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: wallet.type,
        shipping_info: formattedShippingInfo
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to claim device');
    }
    
    return result;
  } catch (error) {
    console.error('Error claiming device:', error);
    return {
      success: false,
      message: error.message || 'Failed to claim device',
    };
  }
};

/**
 * Get shipping status for a claimed order
 * @param {string} orderId - Order ID
 * @param {Object} wallet - Wallet information
 * @returns {Promise<Object>} Response with shipping status
 */
export const getShippingStatus = async (orderId, wallet) => {
  try {
    if (!orderId || !wallet.connected) {
      throw new Error('Order ID or wallet connection missing');
    }
    
    // Get network ID based on wallet network
    const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
    
    // Get signature using signature service
    const signatureData = await signatureService.getValidSignature(
      wallet.address,
      networkId,
      wallet.type
    );
    
    // Make API call to get shipping status
    const response = await fetch(`${API_BASE_URL}/devices/order/${orderId}/shipping-status/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: wallet.address,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: wallet.type
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to get shipping status');
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching shipping status:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch shipping status',
    };
  }
};

/**
 * Get physical devices for a delivered order
 * @param {string} orderId - Order ID
 * @param {Object} wallet - Wallet information
 * @returns {Promise<Object>} Response with physical devices
 */
export const getPhysicalDevices = async (orderId, wallet) => {
  try {
    if (!orderId || !wallet.connected) {
      throw new Error('Order ID or wallet connection missing');
    }
    
    // Get network ID based on wallet network
    const networkId = wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1);
    
    // Get signature using signature service
    const signatureData = await signatureService.getValidSignature(
      wallet.address,
      networkId,
      wallet.type
    );
    
    // Make API call to get physical devices
    const response = await fetch(`${API_BASE_URL}/devices/order/${orderId}/physical-devices/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: wallet.address,
        signature: signatureData.signature,
        message: signatureData.message,
        wallet_type: wallet.type
      }),
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to get physical devices');
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching physical devices:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch physical devices',
    };
  }
};

export default {
  getDeviceDetailsByOrderId,
  getDeviceClaimStatus,
  claimDevice,
  getShippingStatus,
  getPhysicalDevices
};
